import { defineComponent as _defineComponent } from 'vue'
import { computed, ComputedRef, onMounted, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate, ValidationArgs } from '@vuelidate/core';

import { useAuthStore, useUiStore } from '@/stores';
import { UserService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';

import {
    required,
    maxLength,
    minLength,
    containsUppercase,
    containsLowercase,
    containsNumber,
    containsSpecial,
    sameAs,
} from '@/utils/validators';

import { UpdatePasswordFormState, UserModel } from '@/types';
import { AxiosResponse } from 'axios';

const imageLoginPath = '/img/slogan-login.png';
const logoPath = '/img/logomob.png';


export default /*@__PURE__*/_defineComponent({
  __name: 'UpdatePasswordPage',
  setup(__props, { expose: __expose }) {
  __expose();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const uiStore = useUiStore();

const formState = reactive<UpdatePasswordFormState>({
    password: '',
    confirmPassword: '',
});

const token = computed(() => route.query.token as string | undefined);

const rules = computed<ValidationArgs<UpdatePasswordFormState>>(() => ({
    password: {
        required,
        minLength: minLength(8),
        maxLength: maxLength(250),
        containsUppercase,
        containsLowercase,
        containsNumber,
        containsSpecial,
    },
    confirmPassword: {
        required,
        sameAsPassword: sameAs(formState.password, t('user.labels.password')),
    },
}));

const v$ = useVuelidate(rules, formState);

const authStore = useAuthStore();
const user: ComputedRef<UserModel | undefined> = computed(() => authStore.userModel);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const handlePasswordUpdate = async () => {
    const isFormValid = await v$.value.$validate();
    if (!isFormValid) return;

    try {
        setLoading(true);

        if (token.value) {
            const { data }: AxiosResponse = await UserService.resetPassword({
                token: token.value,
                password: formState.password,
            });

            if (data.success) {
                router.push({ name: 'Login' }).then(() => {
                    toast.success(data.message);
                });
            }
        } else {
            const { data }: AxiosResponse = await UserService.overwritePassword(formState);

            if (data.success) {
                localStorage.removeItem('tokenTemp');
                router.push({ name: 'Login' }).then(() => {
                    toast.success(data.message);
                });
            }
        }
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    if (!authStore.userNeedsUpdatePassword) {
        if (user.value !== undefined && user.value.requiresTwoFactor) {
            await router.push({ name: 'TwoFaAuthenticator' });
            return;
        } else if (token.value === null) {
            await router.push({ name: 'Home' });
        }
    }
});

const __returned__ = { imageLoginPath, logoPath, t, route, router, uiStore, formState, token, rules, v$, authStore, user, setLoading, handlePasswordUpdate }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})